import { Box, Flex, HStack, Text } from '@elkaso-app/web-design';
import { usePageParams } from 'hooks/use-page-params';
import { MobileShell } from 'pages/sales-lookup/components/mobile-shell';
import { RestaurantPicker } from 'pages/sales-lookup/components/restaurant-picker';
import { useBasket } from 'pages/sales-lookup/hooks/use-basket';
import { useLogPriceToInquiry } from 'pages/sales-lookup/hooks/use-log-price-to-inquiry';
import { BasketItem, basketItemSummary, basketItemVariantLabel } from 'pages/sales-lookup/utils/basket';
import { buildLoggedItem } from 'pages/sales-lookup/utils/build-inquiry-payload';
import qs from 'query-string';
import { useEffect } from 'react';
import { FiChevronLeft } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

// Review the visit basket and push it through as ONE inquiry. The customer is
// required here (and only here): items can be collected before the rep knows
// who they're logging them for.
export const BasketScreen = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { getPageParams } = usePageParams();
  const params = getPageParams();
  const restaurantId = Number(params.restaurantId) || undefined;
  const restaurantName = (params.restaurantName as string) || '';

  useEffect(() => {
    dispatch(changeTitle('Sales Lookup'));
  }, []);

  const { items, remove, clear } = useBasket();
  const { mutate: submitBasket, isLoading: isSaving } = useLogPriceToInquiry();

  const carriedParams = () => {
    const { restaurantId: rid, restaurantName: rname, vat, search } = getPageParams();
    return { restaurantId: rid, restaurantName: rname, vat, search };
  };

  const goToList = (replace = false) => {
    const location = {
      pathname: '/app/sales-lookup/list',
      search: qs.stringify(carriedParams(), { skipEmptyString: true }),
    };
    if (replace) history.replace(location);
    else history.push(location);
  };

  const edit = (item: BasketItem) => {
    history.push({
      pathname: `/app/sales-lookup/families/${item.familyId}/feedback`,
      search: qs.stringify(
        { ...carriedParams(), variantId: item.variant.id, basketItem: item.key },
        { skipEmptyString: true }
      ),
    });
  };

  const createInquiry = () => {
    if (!restaurantId || !items.length || isSaving) return;
    submitBasket(
      {
        restaurantId,
        items: items.map((item) =>
          buildLoggedItem({ variant: item.variant, headline: item.headline, values: item.values })
        ),
      },
      {
        onSuccess: () => {
          clear();
          goToList(true);
        },
      }
    );
  };

  const canSubmit = !!restaurantId && items.length > 0 && !isSaving;

  return (
    <MobileShell>
      <HStack as='button' spacing='4px' mb='12px' onClick={() => goToList()}>
        <FiChevronLeft />
        <Text fontSize='14px' fontWeight='semibold' color='gray.700'>
          Keep browsing
        </Text>
      </HStack>

      <Text fontSize='11px' fontWeight='semibold' letterSpacing='0.12em' color='gray.500' mb='10px'>
        INQUIRY BASKET
      </Text>

      {!items.length ? (
        <Box pt='24px'>
          <Text fontSize='15px' fontWeight='semibold' color='gray.800' textAlign='center' mb='6px'>
            Your basket is empty
          </Text>
          <Text fontSize='13px' color='gray.500' textAlign='center' mb='16px'>
            Open a product and tap &quot;Add to basket&quot; for everything the customer is interested in.
          </Text>
          <PrimaryButton label='Browse products' onClick={() => goToList()} />
        </Box>
      ) : (
        <>
          {restaurantId ? (
            <Text fontSize='15px' color='gray.800' mb='14px'>
              One inquiry for{' '}
              <Text as='span' fontWeight='bold'>
                {restaurantName || 'the selected customer'}
              </Text>{' '}
              with {items.length === 1 ? '1 item' : `${items.length} items`}.
            </Text>
          ) : (
            <Box mb='14px'>
              <Text fontSize='15px' fontWeight='semibold' color='gray.800' mb='6px'>
                Who is this inquiry for?
              </Text>
              <Text fontSize='13px' color='gray.500' mb='10px'>
                Pick the customer to create the inquiry — the basket is kept until you do.
              </Text>
              <RestaurantPicker onLight />
            </Box>
          )}

          {items.map((item) => (
            <Box
              key={item.key}
              bg='white'
              borderRadius='14px'
              border='1px solid'
              borderColor='gray.100'
              p='14px'
              mb='10px'>
              <Text fontSize='15px' fontWeight='semibold' color='gray.800'>
                {item.familyName}
              </Text>
              <Text fontSize='12px' color='gray.500' mt='2px'>
                {basketItemVariantLabel(item)}
              </Text>
              <Text fontSize='12.5px' color='gray.700' mt='6px'>
                {basketItemSummary(item)}
              </Text>
              <Flex mt='10px' justify='flex-end'>
                <HStack spacing='16px'>
                  <Box as='button' type='button' onClick={() => edit(item)}>
                    <Text fontSize='13px' fontWeight='semibold' color='gray.700'>
                      Edit
                    </Text>
                  </Box>
                  <Box as='button' type='button' onClick={() => remove(item.key)}>
                    <Text fontSize='13px' fontWeight='semibold' color='red.600'>
                      Remove
                    </Text>
                  </Box>
                </HStack>
              </Flex>
            </Box>
          ))}

          <Box mt='16px'>
            <PrimaryButton
              label={isSaving ? 'Creating…' : `Create inquiry (${items.length})`}
              onClick={createInquiry}
              disabled={!canSubmit}
            />
            {!restaurantId && (
              <Text fontSize='12px' color='gray.500' textAlign='center' mt='8px'>
                Select a customer to continue.
              </Text>
            )}
          </Box>
        </>
      )}
    </MobileShell>
  );
};

const PrimaryButton = ({
  label,
  onClick,
  disabled = false,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <Box
    as='button'
    type='button'
    disabled={disabled}
    w='full'
    py='12px'
    borderRadius='12px'
    bg='red.600'
    opacity={disabled ? 0.6 : 1}
    _hover={{ bg: disabled ? 'red.600' : 'red.700' }}
    onClick={onClick}>
    <Text fontSize='15px' fontWeight='bold' color='white'>
      {label}
    </Text>
  </Box>
);
